// Frontend mirror of base44/shared/telemetryPrettify.ts — acts as a safety net
// for any RigLiveFeed records that were stored before the ingest-side
// prettifier was added (or iRacing packets that slipped through). Applied at
// display time in the rig card / detail components.

const PREFIXES = ['ks_', 'acc_', 'ac_'];

const ABBREVIATIONS = {
  bmw: 'BMW',
  gt3: 'GT3',
  gt4: 'GT4',
  cup: 'Cup',
  f1: 'F1',
  gp: 'GP',
};

export const KNOWN_TRACKS = {
  nordschleife: 'Nürburgring Nordschleife',
  nurburgring: 'Nürburgring',
  nur: 'Nürburgring GP',
};

function titleCaseWord(word) {
  if (!word) return word;
  const abbr = ABBREVIATIONS[word];
  if (abbr) return abbr;
  return word.charAt(0).toUpperCase() + word.slice(1);
}

// Both Assetto Corsa games share the same Kunos asset-ID format and need the
// same display-side prettifying, so they're treated as one family.
export function isAcFamily(sim) {
  return sim === 'assetto_corsa' || sim === 'assetto_corsa_competizione';
}

export function prettifyAssettoName(raw) {
  if (!raw || typeof raw !== 'string') return null;
  let s = raw.trim().toLowerCase();
  if (!s) return null;
  for (const p of PREFIXES) {
    if (s.startsWith(p)) { s = s.slice(p.length); break; }
  }
  const known = KNOWN_TRACKS[s];
  if (known) return known;
  // Insert a space between 2+ trailing letters and 2+ leading digits so
  // concatenated model names format correctly (e.g. "abarth500" -> "abarth 500",
  // "ferrari488" -> "ferrari 488") without splitting single-letter designators
  // like "R8", "M5", or abbreviations like "GT3".
  s = s.replace(/([a-z]{2,})(\d{2,})/g, '$1 $2');
  const words = s.replace(/[_-]+/g, ' ').split(' ').filter(Boolean);
  if (words.length === 0) return null;
  return words.map(titleCaseWord).join(' ');
}

export function prettifyDriverName(raw, rigKey) {
  if (!raw || typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  const lower = trimmed.toLowerCase();
  if (!lower) return null;
  if (rigKey && lower === rigKey.toLowerCase()) return 'Guest Driver';
  if (lower === 'player') return 'Guest Driver';
  if (/^rig[_-]?\d+$/.test(lower)) return 'Guest Driver';
  return trimmed;
}

/**
 * Returns a display-ready value for a telemetry field. If the value already
 * looks clean (contains spaces or mixed case — i.e. it was prettified at
 * ingest), it's returned as-is; otherwise the prettifier is applied.
 */
export function displayAssettoName(raw, rigKey) {
  const prettified = prettifyAssettoName(raw);
  // If the stored value differs from the prettified version, the stored
  // value was already cleaned (or came from iRacing) — keep it.
  if (raw && typeof raw === 'string' && raw.includes(' ') && raw !== prettified) {
    return raw;
  }
  return prettified;
}